import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import DocumentType from 'modules/core/components/DocumentType'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import buildUrl from 'modules/core/lib/buildUrl'
import { formatTime } from 'modules/core/lib/formatTime'
import i18n from 'modules/core/lib/i18n'

import DocumentModal from 'modules/documents/components/DocumentModal'
import SignatureStatusTag from 'modules/documents/components/Documents/List/Item/SignatureStatusTag'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'

interface Props {
  document: Document
  documents: Document[]
}

const DocumentFile = ({ document, documents }: Props) => {
  const [openViewer, setOpenViewer] = useState(false)
  const { access } = useCurrentSession()

  const uploadBy = () => {
    const author = document.author

    if (!author) return null

    const authorName = author.fullName

    return ` - ${i18n.t('documents.uploaded_by', {
      authorName,
    })}`
  }

  const formattedDatetime = () => {
    const date = moment.unix(document.arCreatedAt ?? 0)
    const formattedDate = date.format('ll')
    const formattedTime = formatTime(access, date.format('HH:mm'))

    return `${formattedDate}, ${formattedTime}`
  }

  const showSignatureStatus = document.signatureStatus

  return (
    <Box
      width="full"
      flexDirection="row"
      padding="s16"
      alignItems="center"
      justifyContent="spaceBetween"
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
      borderRadius={{
        topLeft: 'abs008',
        topRight: 'abs008',
        bottomLeft: 'abs008',
        bottomRight: 'abs008',
      }}
    >
      <Box flexDirection="row" justifyContent="spaceBetween" gap="s16" width="full">
        <Box flexDirection="row" alignItems="center" gap="s16" overflowX="hidden">
          <DocumentType mimetype={'pdf'} />
          <Box minWidth="none">
            <Text.Normal weight="medium" ellipsis>
              {document.filename}
            </Text.Normal>
            <Box paddingTop="s4">
              <Text.Normal size="200" color="grey800" ellipsis>
                {formattedDatetime()}
                {uploadBy()}
              </Text.Normal>
            </Box>
          </Box>
        </Box>
        <Box flexDirection="row" alignItems="center">
          {showSignatureStatus && document.signatureStatus && (
            <Box paddingX="s8">
              <SignatureStatusTag status={document.signatureStatus} />
            </Box>
          )}

          {openViewer && (
            <DocumentModal
              documentId={document.id.toString()}
              documents={documents}
              onClose={() => setOpenViewer(false)}
            />
          )}

          <Box paddingRight="s8">
            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <Button type="secondary" iconName="ellipsis" />
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="end">
                <PopoverMenu.HrefOption
                  href={buildUrl(document.file)}
                  title={i18n.t('documents.download')}
                />
              </PopoverMenu.Content>
            </PopoverMenu>
          </Box>

          <Button
            type="secondary"
            aria-label={i18n.t('common.view_document')}
            onClick={() => setOpenViewer(true)}
            iconName="arrow-right"
          />
        </Box>
      </Box>
    </Box>
  )
}

export default observer(DocumentFile)
